import { useMemo, useState, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import ceoxSettingsService, { SQLTargets } from '@/services/ceoxSettings';
import googleAdsService, { GoogleAdsOverview } from '@/services/googleAds';
import pipedriveService, {
  PipedriveConnectionStatus,
  PipedriveDeal,
  PipedriveDealStage,
  PipedriveLead,
  QUALIFIED_SQL_IDS,
  CHANNEL_OPTIONS,
} from '@/services/pipedrive';
import CEOxDashboardComponent, {
  DateFilter,
  DatePreset,
  PipedriveChannelRow,
} from './CEOxDashboard.component';

// ── MOCK (remove after Google Ads verification) ──────────────────────
const USE_GOOGLE_ADS_MOCK = true;

const MOCK_GOOGLE_ADS_OVERVIEW: GoogleAdsOverview = {
  totalImpressions: 1630400,
  totalClicks: 39482,
  totalCost: 46588.2,
  totalConversions: 994,
  totalConversionValue: 165470,
  avgCpc: 1.18,
  avgCpa: 46.87,
  avgCtr: 2.42,
  avgCvr: 2.52,
  campaigns: [],
  lastSyncedAt: new Date().toISOString(),
};
// ── END MOCK ─────────────────────────────────────────────────────────

function getYearRange(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(target.getFullYear(), target.getMonth() + 1, 0);
  return {
    startDate: target.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const end = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

const CEOxDashboardContainer = function (): JSX.Element {
  // Date filter
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    preset: 'this_month',
    ...getMonthRange(0),
  });

  const handlePresetChange = useCallback((preset: DatePreset) => {
    const year = new Date().getFullYear();
    switch (preset) {
      case 'this_month':
        setDateFilter({ preset, ...getMonthRange(0) });
        break;
      case 'last_month':
        setDateFilter({ preset, ...getMonthRange(-1) });
        break;
      case 'this_quarter':
        setDateFilter({ preset, ...getQuarterRange() });
        break;
      case 'this_year':
        setDateFilter({ preset, ...getYearRange(year) });
        break;
      case 'last_year':
        setDateFilter({ preset, ...getYearRange(year - 1) });
        break;
      default:
        setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, []);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate });
  }, []);

  // Pipedrive connection status
  const { data: pipedriveStatus } = useSeoFetch<PipedriveConnectionStatus>(
    ['ceox-pipedrive-status'],
    () => pipedriveService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = pipedriveStatus?.isConnected ?? false;

  // Pipedrive deals
  const { data: deals = [], isLoading: isDealsLoading } = useSeoFetch<PipedriveDeal[]>(
    ['ceox-deals'],
    () => pipedriveService.fetchDeals(),
    { retry: false, enabled: isConnected }
  );

  // Pipedrive stages
  const { data: stages = [] } = useSeoFetch<PipedriveDealStage[]>(
    ['ceox-stages'],
    () => pipedriveService.fetchStages(),
    { retry: false, enabled: isConnected }
  );

  // Pipedrive leads (top of funnel)
  const { data: leads = [], isLoading: isLeadsLoading } = useSeoFetch<PipedriveLead[]>(
    ['ceox-leads'],
    () => pipedriveService.fetchLeads(),
    { retry: false, enabled: isConnected }
  );

  // Google Ads (for ad spend enrichment)
  const { data: googleAdsStatus } = useSeoFetch(
    ['ceox-dash-gads-status'],
    () => googleAdsService.getConnectionStatus(),
    { retry: false, enabled: !USE_GOOGLE_ADS_MOCK }
  );

  const isGoogleAdsConnected = USE_GOOGLE_ADS_MOCK || (googleAdsStatus?.isConnected ?? false);

  const { data: googleAdsOverview } = useSeoFetch<GoogleAdsOverview>(
    ['ceox-dash-gads-overview', dateFilter.startDate, dateFilter.endDate],
    () => googleAdsService.getOverview(dateFilter.startDate, dateFilter.endDate),
    { retry: false, enabled: isGoogleAdsConnected && !USE_GOOGLE_ADS_MOCK }
  );

  const googleAdsSpend = useMemo(() => {
    const overview = USE_GOOGLE_ADS_MOCK ? MOCK_GOOGLE_ADS_OVERVIEW : googleAdsOverview ?? null;
    return overview?.totalCost ?? 0;
  }, [googleAdsOverview]);

  // SQL targets from settings
  const { data: sqlTargets } = useSeoFetch<SQLTargets>(
    ['ceox-sql-targets'],
    () => ceoxSettingsService.getSQLTargets(),
    { retry: false }
  );

  // Map stage IDs to names
  const stageMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of stages) {
      map.set(s.id, s.name);
    }
    return map;
  }, [stages]);

  // Enrich deals with stage names
  const enrichedDeals = useMemo(
    () =>
      deals.map((d) => ({
        ...d,
        stageName: stageMap.get(d.stageId) ?? `Stage ${d.stageId}`,
      })),
    [deals, stageMap]
  );

  // Filter deals by date range
  const filteredDeals = useMemo(() => {
    const start = dateFilter.startDate;
    const end = dateFilter.endDate;
    return enrichedDeals.filter((d) => {
      const addDate = d.addTime?.split(' ')[0] ?? d.addTime?.split('T')[0] ?? '';
      return addDate >= start && addDate <= end;
    });
  }, [enrichedDeals, dateFilter.startDate, dateFilter.endDate]);

  // Filter leads by date range
  const filteredLeads = useMemo(() => {
    const start = dateFilter.startDate;
    const end = dateFilter.endDate;
    return leads.filter((l) => {
      const addDate = l.createdAt?.split(' ')[0] ?? l.createdAt?.split('T')[0] ?? '';
      return addDate >= start && addDate <= end;
    });
  }, [leads, dateFilter.startDate, dateFilter.endDate]);

  // ── Build per-channel rows from Leads + Deals ────────────────────
  // Leads = top of funnel (channel breakdown, SQL stage progression)
  // Deals = bottom of funnel (won/lost/value)
  const channelRows: PipedriveChannelRow[] = useMemo(() => {
    const t = sqlTargets ?? { seo: 20, google_ads: 50, cold_email: 30, pipeline: 40 };

    const channelTargetMap: Record<number, number> = {
      195: t.cold_email, // Cold Email
      196: t.pipeline, // Cold Call
      224: t.pipeline, // Referral
      98: t.seo, // Inbound
      223: t.google_ads, // Google Ads
      97: t.cold_email, // ActiveCampaign
      106: t.pipeline, // Fintech Engineer
    };

    // Qualified SQL stage IDs (Qualified, Discovery Call Booked, Sent Profiles)
    const QUALIFIED_STAGE_IDS = [212, 213, 218];

    // Group leads by channel
    const leadsByChannel = new Map<number | null, PipedriveLead[]>();
    for (const lead of filteredLeads) {
      const key = lead.channelId;
      const existing = leadsByChannel.get(key) ?? [];
      existing.push(lead);
      leadsByChannel.set(key, existing);
    }

    // Group deals by channel (for won/lost enrichment)
    const dealsByChannel = new Map<number | null, PipedriveDeal[]>();
    for (const deal of filteredDeals) {
      const key = deal.channelId;
      const existing = dealsByChannel.get(key) ?? [];
      existing.push(deal);
      dealsByChannel.set(key, existing);
    }

    // Collect all channel IDs from both leads and deals
    const allChannelIds = new Set<number | null>();
    for (const key of leadsByChannel.keys()) allChannelIds.add(key);
    for (const key of dealsByChannel.keys()) allChannelIds.add(key);

    const rows: PipedriveChannelRow[] = [];

    for (const channelId of allChannelIds) {
      const channelLeads = leadsByChannel.get(channelId) ?? [];
      const channelDeals = dealsByChannel.get(channelId) ?? [];
      const channelName =
        channelId !== null ? CHANNEL_OPTIONS[channelId] ?? `Channel ${channelId}` : 'Uncategorized';

      // SQLs = leads that reached a qualified stage
      const sqlLeads = channelLeads.filter(
        (l) => l.sqlStageId !== null && QUALIFIED_STAGE_IDS.includes(l.sqlStageId)
      );

      // Also count qualified deals
      const sqlDeals = channelDeals.filter(
        (d) => d.isSQLId !== null && QUALIFIED_SQL_IDS.includes(d.isSQLId)
      );

      const won = channelDeals.filter((d) => d.status === 'won');
      const lost = channelDeals.filter((d) => d.status === 'lost');

      const spend = channelId === 223 ? googleAdsSpend : 0;
      const totalSQLs = sqlLeads.length + sqlDeals.length;

      rows.push({
        channel: channelName,
        channelId: channelId ?? null,
        totalLeads: channelLeads.length,
        totalDeals: channelDeals.length,
        sqls: totalSQLs,
        sqlTarget: channelId !== null ? channelTargetMap[channelId] ?? t.pipeline : t.pipeline,
        won: won.length,
        wonValue: won.reduce((s, d) => s + d.value, 0),
        lost: lost.length,
        lostValue: lost.reduce((s, d) => s + d.value, 0),
        open: channelDeals.filter((d) => d.status === 'open').length,
        spend,
        cpl: totalSQLs > 0 && spend > 0 ? spend / totalSQLs : 0,
      });
    }

    rows.sort((a, b) => b.totalLeads - a.totalLeads);
    return rows;
  }, [filteredLeads, filteredDeals, sqlTargets, googleAdsSpend]);

  return (
    <CEOxDashboardComponent
      isPipedriveConnected={isConnected}
      deals={enrichedDeals}
      filteredDeals={filteredDeals}
      filteredLeads={filteredLeads}
      stages={stages}
      isLoading={isDealsLoading || isLeadsLoading}
      channelRows={channelRows}
      sqlTargets={sqlTargets ?? null}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
    />
  );
};

export default CEOxDashboardContainer;
